import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Sparkles, FileDown, Presentation, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { generateLessonPackage, type LessonPackage } from "@/lib/lesson-generator.functions";
import { downloadLessonPDF, downloadLessonPPTX } from "@/lib/lesson-exports";

export const Route = createFileRoute("/teacher/generator")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "AI Lesson Generator · MAKAI Teachers" }] }),
  component: Page,
});

const DURATIONS = [30, 40, 60, 80, 90, 120];

function Page() {
  const ctx = useTeacherContext();
  const generate = useServerFn(generateLessonPackage);
  const [subject, setSubject] = useState("");
  const [className, setClassName] = useState("");
  const [topic, setTopic] = useState("");
  const [duration, setDuration] = useState(40);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [pkg, setPkg] = useState<LessonPackage | null>(null);

  const subjectOptions = Array.from(new Set([...(ctx.subjects.map((s) => s.name))])).filter(Boolean);
  const classOptions = Array.from(new Set(ctx.streams.map((s) => `${s.class_name} ${s.name}`)));

  async function run() {
    if (!subject || !className || !topic) {
      toast.error("Please fill subject, class and topic.");
      return;
    }
    setBusy(true);
    setPkg(null);
    const t = toast.loading("MAKAI is designing your lesson…");
    try {
      const out = await generate({ data: { subject, className, topic, durationMins: duration, notes: notes || undefined } });
      setPkg(out);
      toast.success("Lesson package ready", { id: t });
    } catch (e) {
      toast.error((e as Error).message ?? "Generation failed", { id: t });
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell role="teacher" title="AI Lesson Generator" subtitle="MAKAI builds a full NCDC-aligned lesson in seconds">
      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        <Card className="h-fit lg:sticky lg:top-24">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><Wand2 className="h-4 w-4 text-primary" /> Lesson brief</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label>Subject</Label>
              {subjectOptions.length ? (
                <Select value={subject} onValueChange={setSubject}>
                  <SelectTrigger><SelectValue placeholder="Choose subject" /></SelectTrigger>
                  <SelectContent>
                    {subjectOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    <SelectItem value="__other">Other…</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Mathematics" />
              )}
              {subject === "__other" && <Input className="mt-2" placeholder="Type subject" onChange={(e) => setSubject(e.target.value)} />}
            </div>
            <div className="space-y-1.5">
              <Label>Class</Label>
              {classOptions.length ? (
                <Select value={className} onValueChange={setClassName}>
                  <SelectTrigger><SelectValue placeholder="Choose class" /></SelectTrigger>
                  <SelectContent>
                    {classOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    <SelectItem value="__other">Other…</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input value={className} onChange={(e) => setClassName(e.target.value)} placeholder="e.g. P5 or S2" />
              )}
              {className === "__other" && <Input className="mt-2" placeholder="Type class" onChange={(e) => setClassName(e.target.value)} />}
            </div>
            <div className="space-y-1.5">
              <Label>Topic</Label>
              <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. The water cycle" />
            </div>
            <div className="space-y-1.5">
              <Label>Duration</Label>
              <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DURATIONS.map((d) => <SelectItem key={d} value={String(d)}>{d} minutes</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Extra notes (optional)</Label>
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Prior knowledge, special learners, focus skills…" />
            </div>
            <Button onClick={run} disabled={busy} className="w-full gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {busy ? "Generating…" : "Generate lesson"}
            </Button>
            <p className="text-xs text-muted-foreground">Aligned with the Uganda NCDC competency-based curriculum.</p>
          </CardContent>
        </Card>

        <div className="min-w-0">
          {!pkg && !busy && (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center gap-3 py-16 text-center text-muted-foreground">
                <Sparkles className="h-10 w-10 text-primary/60" />
                <p className="max-w-md text-sm">Fill the brief on the left. MAKAI will draft a full lesson plan, slides, worksheet, quiz, homework and marking guide you can edit and export.</p>
              </CardContent>
            </Card>
          )}
          {busy && (
            <Card><CardContent className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm">Designing your lesson — this takes ~15s.</p>
            </CardContent></Card>
          )}
          {pkg && <Result pkg={pkg} />}
        </div>
      </div>
    </AppShell>
  );
}

function Result({ pkg }: { pkg: LessonPackage }) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
          <div className="space-y-1 min-w-0">
            <CardTitle className="text-xl">{pkg.meta.topic}</CardTitle>
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="secondary">{pkg.meta.subject}</Badge>
              <Badge variant="secondary">{pkg.meta.class}</Badge>
              <Badge variant="secondary">{pkg.meta.durationMins} min</Badge>
              <Badge>{pkg.meta.curriculum}</Badge>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => downloadLessonPDF(pkg)}><FileDown className="h-4 w-4" /> PDF</Button>
            <Button size="sm" className="gap-1.5" onClick={() => downloadLessonPPTX(pkg)}><Presentation className="h-4 w-4" /> Slides</Button>
          </div>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">{pkg.overview}</CardContent>
      </Card>

      <Tabs defaultValue="plan" className="w-full">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="plan">Plan</TabsTrigger>
          <TabsTrigger value="slides">Slides</TabsTrigger>
          <TabsTrigger value="worksheet">Worksheet</TabsTrigger>
          <TabsTrigger value="quiz">Quiz</TabsTrigger>
          <TabsTrigger value="homework">Homework</TabsTrigger>
          <TabsTrigger value="marking">Marking</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="plan" className="space-y-4 pt-4">
          <Section title="Objectives"><Bullets items={pkg.objectives} /></Section>
          <Section title="Competencies"><Bullets items={pkg.competencies} /></Section>
          <Section title="Materials"><Bullets items={pkg.materials} /></Section>
          <Section title="Key vocabulary">
            <ul className="space-y-1.5 text-sm">{pkg.vocabulary.map((v) => <li key={v.term}><strong>{v.term}</strong> — {v.meaning}</li>)}</ul>
          </Section>
          <Section title="Lesson flow">
            <div className="space-y-2">
              {pkg.lessonPlan.map((ph, i) => (
                <div key={i} className="rounded-lg border p-3">
                  <div className="flex items-center justify-between mb-1.5"><strong>{ph.phase}</strong><Badge variant="outline">{ph.minutes} min</Badge></div>
                  <p className="text-sm"><span className="font-medium text-primary">Teacher:</span> {ph.teacherActivity}</p>
                  <p className="text-sm mt-1"><span className="font-medium text-primary">Learners:</span> {ph.learnerActivity}</p>
                </div>
              ))}
            </div>
          </Section>
          <Section title="Differentiation">
            <div className="grid gap-3 sm:grid-cols-2">
              <div><h4 className="text-sm font-semibold mb-1">Support</h4><Bullets items={pkg.differentiation.support} /></div>
              <div><h4 className="text-sm font-semibold mb-1">Extension</h4><Bullets items={pkg.differentiation.extension} /></div>
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="slides" className="space-y-3 pt-4">
          {pkg.slides.map((s, i) => (
            <Card key={i}>
              <CardHeader className="pb-2"><CardTitle className="text-base">Slide {i + 1} · {s.title}</CardTitle></CardHeader>
              <CardContent className="text-sm">
                <Bullets items={s.bullets} />
                {s.speakerNotes && <p className="mt-2 rounded bg-muted/50 p-2 text-xs italic text-muted-foreground"><strong>Speaker notes:</strong> {s.speakerNotes}</p>}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="worksheet" className="pt-4">
          <Section title="Instructions"><p className="text-sm">{pkg.worksheet.instructions}</p></Section>
          <Section title="Tasks">
            <ol className="space-y-2 text-sm">
              {pkg.worksheet.tasks.map((t) => (
                <li key={t.number} className="flex justify-between gap-3"><span><strong>{t.number}.</strong> {t.question}</span><Badge variant="outline" className="shrink-0">{t.marks} mk</Badge></li>
              ))}
            </ol>
          </Section>
        </TabsContent>

        <TabsContent value="quiz" className="space-y-3 pt-4">
          {pkg.quiz.map((q, i) => (
            <Card key={i}><CardContent className="pt-4 text-sm">
              <p className="font-medium mb-2">{i + 1}. {q.question}</p>
              <ul className="space-y-1">
                {q.options.map((o, oi) => (
                  <li key={oi} className={oi === q.answerIndex ? "text-primary font-medium" : ""}>{String.fromCharCode(65 + oi)}. {o}{oi === q.answerIndex && " ✓"}</li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-muted-foreground"><strong>Why:</strong> {q.explanation}</p>
            </CardContent></Card>
          ))}
        </TabsContent>

        <TabsContent value="homework" className="pt-4">
          <Section title="Instructions"><p className="text-sm">{pkg.homework.instructions}</p></Section>
          <Section title="Tasks"><Bullets items={pkg.homework.tasks} /></Section>
        </TabsContent>

        <TabsContent value="marking" className="pt-4">
          <Section title="Marking guide">
            <ul className="space-y-2 text-sm">
              {pkg.markingGuide.map((m, i) => (
                <li key={i} className="rounded border p-2">
                  <div className="flex justify-between gap-3"><strong>{m.item}</strong><Badge variant="outline">{m.marks} mk</Badge></div>
                  <p className="text-muted-foreground mt-1">{m.expectedAnswer}</p>
                </li>
              ))}
            </ul>
          </Section>
        </TabsContent>

        <TabsContent value="notes" className="space-y-4 pt-4">
          <Section title="Teaching notes"><p className="text-sm whitespace-pre-wrap">{pkg.teachingNotes}</p></Section>
          <Section title="Reflection prompts"><Bullets items={pkg.reflection} /></Section>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card><CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">{title}</CardTitle></CardHeader><CardContent>{children}</CardContent></Card>
  );
}
function Bullets({ items }: { items: string[] }) {
  return <ul className="list-disc pl-5 space-y-1 text-sm">{items.map((it, i) => <li key={i}>{it}</li>)}</ul>;
}
